import {
  asc,
  desc,
  eq,
  isNotNull,
  like,
  or,
  type PowerSyncDrizzleDB,
  recipe,
  recipe_ingredient,
  recipe_version,
} from "@mealplan/powersync";
import { and } from "drizzle-orm";
import { liveRecipeFilters } from "../live/filters";

/** Parses a JSON-array field (steps/tags) tolerantly despite dirty data. */
export function parseStringArray(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === "string");
    }
    return [];
  } catch {
    return [];
  }
}

export function serializeStringArray(values: string[]): string {
  return JSON.stringify(values.map((v) => v.trim()).filter(Boolean));
}

/**
 * User recipes, optionally filtered by text (name) and tag.
 * The tag filter uses LIKE on the JSON (`"tag"`): sufficiently exact
 * for F1 because tags are serialized with quotes.
 */
export function getRecipesQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  options: { search?: string; tag?: string } = {},
) {
  // Live view: excludes recipes in the trash (via the centralized live layer).
  const conditions = [eq(recipe.user_id, userId), ...liveRecipeFilters()];
  const search = options.search?.trim();
  if (search) {
    const pattern = `%${search}%`;
    const nameOrTag = or(like(recipe.name, pattern), like(recipe.tags, pattern));
    if (nameOrTag) {
      conditions.push(nameOrTag);
    }
  }
  if (options.tag) {
    conditions.push(like(recipe.tags, `%"${options.tag}"%`));
  }
  return db
    .select()
    .from(recipe)
    .where(and(...conditions))
    .orderBy(desc(recipe.updated_at));
}

export function getRecipeByIdQuery(db: PowerSyncDrizzleDB, recipeId: string) {
  return db.select().from(recipe).where(eq(recipe.id, recipeId)).limit(1);
}

export function getRecipeIngredientsQuery(db: PowerSyncDrizzleDB, recipeId: string) {
  return db
    .select()
    .from(recipe_ingredient)
    .where(eq(recipe_ingredient.recipe_id, recipeId))
    .orderBy(asc(recipe_ingredient.sort_order), asc(recipe_ingredient.created_at));
}

/** All LIVE recipes with their ingredients (for shopping list generation).
 * Trashed recipes do NOT feed shopping list generation. */
export function getAllRecipesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(recipe)
    .where(and(eq(recipe.user_id, userId), ...liveRecipeFilters()));
}

/** Recipes in the trash (deleted_at IS NOT NULL), newest first. */
export function getTrashedRecipesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(recipe)
    .where(and(eq(recipe.user_id, userId), isNotNull(recipe.deleted_at)))
    .orderBy(desc(recipe.deleted_at));
}

export function getAllIngredientsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(recipe_ingredient).where(eq(recipe_ingredient.user_id, userId));
}

/**
 * LOCAL version history of a recipe (L4) — PowerSync bucket bounded to
 * the sync window (latest 10). id UUIDv7 ⇒ descending order = newest
 * first. Deep history is requested on demand via the API.
 */
export function getRecipeVersionsQuery(db: PowerSyncDrizzleDB, recipeId: string) {
  return db
    .select()
    .from(recipe_version)
    .where(eq(recipe_version.recipe_id, recipeId))
    .orderBy(desc(recipe_version.id));
}

/**
 * User recipes containing an ingredient whose name matches
 * (LIKE, case-insensitive via SQLite's default COLLATE NOCASE in LIKE).
 * Interconnection: ingredient/pantry → recipes that use it (Phase 5, axis 6).
 */
export function getRecipesByIngredientNameQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  ingredientName: string,
) {
  const pattern = `%${ingredientName.trim()}%`;
  return db
    .selectDistinct({
      id: recipe.id,
      name: recipe.name,
      servings: recipe.servings,
      photo_url: recipe.photo_url,
    })
    .from(recipe_ingredient)
    .innerJoin(recipe, eq(recipe_ingredient.recipe_id, recipe.id))
    .where(
      and(
        eq(recipe.user_id, userId),
        ...liveRecipeFilters(),
        like(recipe_ingredient.name, pattern),
      ),
    )
    .orderBy(desc(recipe.updated_at));
}

export type RecipeByIngredientRow = Awaited<
  ReturnType<ReturnType<typeof getRecipesByIngredientNameQuery>["execute"]>
>[number];
